
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Settings, Crown, Loader2 } from 'lucide-react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DashboardHeaderProps {
  user: User | null;
  isPremium?: boolean;
}

export function DashboardHeader({ user, isPremium }: DashboardHeaderProps) {
  const [portalLoading, setPortalLoading] = useState(false);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (err: any) {
      toast.error('Failed to open billing portal', { description: err.message });
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
      <div>
        <h1 className="text-3xl font-bold text-grandpal-green mb-2">My Dashboard</h1>
        <p className="text-gray-600 flex items-center gap-2 flex-wrap">
          Welcome back, {user?.user_metadata?.first_name || 'there'}!
          {isPremium && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
              <Crown className="h-3 w-3" />
              Premium
            </span>
          )}
        </p>
        <p className="text-gray-500 text-sm mt-0.5">Manage your nursing home search process.</p>
      </div>

      <div className="mt-4 md:mt-0 flex items-center gap-2 flex-wrap">
        {isPremium && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleManageSubscription}
            disabled={portalLoading}
            className="border-primary/40 text-primary hover:bg-primary/5"
          >
            {portalLoading ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Crown className="h-4 w-4 mr-1.5" />
            )}
            Manage subscription
          </Button>
        )}
        <Button asChild variant="outline" size="sm">
          <Link to="/settings">
            <Settings className="h-4 w-4 mr-1.5" />
            Settings
          </Link>
        </Button>
        <Button asChild className="bg-grandpal-green hover:bg-grandpal-green/90">
          <a href="/nursing-homes">Explore Homes</a>
        </Button>
      </div>
    </div>
  );
}
